import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  TextField,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { apiClientDrawerV2 as apiClient } from '@lift-admin/utils';
import { Application } from "@lift-admin/common/apiTypes";
import { generateUUID } from "@lift-admin/utils/uuid";
import { Info, Edit, ChevronDown } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "lift-ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "lift-ui";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Toastify } from 'lift-ui';
import {
  Button,
  DataTable,
  Badge,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { actionLog } from "../../../App";
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from "@lift-admin/utils/logChangeFormatter";
import BrandMultiSelect from "@/features/drawer-structure-v2/drawer-parts/brand-multiselect";
import OrganizationMultiSelect from "@/features/drawer-structure-v2/drawer-parts/organization-multiselect";
import {
  buildAccessLogChanges,
  inferNextAccessModeFromSignedIds,
} from "@/features/drawer-structure-v2/drawer-parts/accessLogHelpers";
import { formatOrderIndex } from "@/features/drawer-structure-v2/drawer-parts/displayPositionLogHelpers";

const APPLICATION_FIELD_SPECS: FieldSpec[] = [
  { key: "name", label: "Name" },
  { key: "description", label: "Description" },
  {
    key: "is_active",
    label: "Status",
    type: "boolean",
    boolStyle: "activeInactive",
  },
  { key: "top_bar_color", label: "Top Bar Color" },
  {
    key: "omit_org_brand_from_url",
    label: "Omit org/brand from URL",
    type: "boolean",
    boolStyle: "yesNo",
  },
  {
    key: "exclude_from_default_application",
    label: "Exclude from default application",
    type: "boolean",
    boolStyle: "yesNo",
  },
  {
    key: "display_order",
    label: "Display Order",
    type: "number",
    resolve: (order) => formatOrderIndex(order),
  },
  { key: "logo", label: "Logo" },
];

/**
 * An application has nothing above it to inherit from, so this flag stays a
 * plain two-state switch here. It is still the base value that channels inherit
 * when they carry no override of their own, so we avoid writing a redundant
 * `false` for applications that never opted in: an absent key and a stored
 * `false` mean the same thing at the application level, and not storing one
 * keeps app_metadata free of keys nobody asked for.
 */
const readStoredOmitOrgBrand = (data: any): boolean =>
  data?.omit_org_brand_from_url ?? data?.metadata?.omit_org_brand_from_url ?? false;

const readStoredExcludeFromDefault = (data: any): boolean =>
  data?.exclude_from_default_application ??
  data?.metadata?.exclude_from_default_application ??
  false;

const theme = createTheme({
  palette: {
    primary: {
      main: "#000",
    },
    secondary: {
      main: "rgba(0, 0, 0, 0)",
    },
  },
  typography: {
    fontFamily: '"Figtree", sans-serif',
  },
});

interface ItemsProps {
  toggle: string;
  searchText: string;
  brands: any[];
  organizations: any[];
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

interface FormStateEdit {
  name: string;
  description: string;
  is_active: boolean;
  brands_exclude: number[];
  organizations_exclude: number[];
  display_order: number;
  logo: string;
  top_bar_color: string;
  omit_org_brand_from_url: boolean;
  exclude_from_default_application: boolean;
}

interface FormStateAdd {
  name: string;
  description: string;
  is_active: boolean;
  display_order: number;
  logo: string;
  top_bar_color: string;
  omit_org_brand_from_url: boolean;
  exclude_from_default_application: boolean;
}

// Helper function to render brand badges
const renderBrandBadges = (data: any) => {
  if (
    !data ||
    !data.access?.brand_level ||
    !data.access.brand_level.brands ||
    !Array.isArray(data.access.brand_level.brands) ||
    data.access.brand_level.brands.length === 0
  ) {
    return <span className="text-gray-400">--</span>;
  }

  const brandIds = data.access.brand_level.brands;
  const access_type = data.access.brand_level.access_mode;
  const brandNames = brandIds.map((brand: any) => brand.name);

  return (
    <div className="flex flex-wrap gap-1 max-h-16 overflow-auto">
      {brandNames.map((name: string, index: number) => (
        <Badge
          key={index}
          variant={access_type === "EXCLUDE" ? "destructive" : "default"}
        >
          {name}
        </Badge>
      ))}
    </div>
  );
};

// Helper function to render organization badges
const renderOrganizationBadges = (data: any) => {
  if (
    !data ||
    !data.access?.organization_level ||
    !data.access.organization_level.organizations ||
    !Array.isArray(data.access.organization_level.organizations) ||
    data.access.organization_level.organizations.length === 0
  ) {
    return <span className="text-gray-400">--</span>;
  }

  const organizationIds = data.access.organization_level.organizations;
  const access_type = data.access.organization_level.access_mode;
  const organizationNames = organizationIds.map((org: any) => org.name);

  return (
    <div className="flex flex-wrap gap-1 max-h-16 overflow-auto">
      {organizationNames.map((name: string, index: number) => (
        <Badge
          key={index}
          variant={access_type === "EXCLUDE" ? "destructive" : "default"}
        >
          {name}
        </Badge>
      ))}
    </div>
  );
};

// Helper function to render status badge
const renderStatusBadge = (isActive: boolean) => {
  return (
    <Badge variant={isActive ? "default" : "secondary"}>
      {isActive ? "Active" : "Inactive"}
    </Badge>
  );
};

// Helper function to format date
const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const Applications = ({
  toggle,
  searchText,
  brands,
  organizations,
  openDialog,
  setOpenDialog,
  hasEditAccess = true,
  hasManageAccess = true,
}: ItemsProps) => {
  const showActionsColumn = hasEditAccess || hasManageAccess;
  const [loadData, setLoadData] = useState<boolean>(false);
  const [applications, setApplications] = useState<Application[]>([]);
  const [applicationsBackup, setApplicationsBackup] = useState<Application[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState<boolean>(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [currentData, setCurrentData] = useState<Application | null>(null);
  const [searchedResults, setSearchedResults] = useState<Application[]>([]);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  
  const [formStateEdit, setFormStateEdit] = useState<FormStateEdit>({
    name: "",
    description: "",
    is_active: true,
    brands_exclude: [],
    organizations_exclude: [],
    display_order: 0,
    logo: "",
    top_bar_color: "#16306D",
    omit_org_brand_from_url: false,
    exclude_from_default_application: false,
  });
  const [formStateAdd, setFormStateAdd] = useState<FormStateAdd>({
    name: "",
    description: "",
    is_active: true,
    display_order: 0,
    logo: "",
    top_bar_color: "#16306D",
    omit_org_brand_from_url: false,
    exclude_from_default_application: false,
  });

  const getApplications = async () => {
    setLoadData(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const res = await apiClient.get("/admin/applications", {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });
      const applicationsData: Application[] = res?.data || [];

      const sortedApplications = applicationsData.sort(
        (a: Application, b: Application) => {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }
      );
      setApplications(sortedApplications);
      setApplicationsBackup(sortedApplications);
    } catch (error) {
      console.error("Error fetching applications:", error);
      Toastify("error", "Applications not fetched");
    } finally {
      setLoadData(false);
    }
  };

  useEffect(() => {
    getApplications();
  }, []);

  const rowData = useMemo(() => applications, [applications]);

  // Pagination calculations
  const paginatedData = useMemo(() => {
    return rowData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [rowData, page, rowsPerPage]);

  const totalPages = Math.ceil(rowData.length / rowsPerPage);

  // Add Application Handler
  const handleAddApplication = async () => {
    try {
      const applicationData: any = {
        id: generateUUID(),
        name: formStateAdd.name,
        description: formStateAdd.description,
        is_active: formStateAdd.is_active,
        display_order: formStateAdd.display_order,
        logo: formStateAdd.logo,
        top_bar_color: formStateAdd.top_bar_color || null,
      };

      // A new application has no stored value, so only an opt-in is worth
      // writing. Leaving the key out avoids a redundant stored `false`.
      if (formStateAdd.omit_org_brand_from_url) {
        applicationData.omit_org_brand_from_url = true;
      }
      if (formStateAdd.exclude_from_default_application) {
        applicationData.exclude_from_default_application = true;
      }

      const res = await apiClient.post("/admin/applications", applicationData);
      Toastify("success", "Application created successfully");
      getApplications();
      handleAddDialogClose();

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const desc = {
        message: buildSummary("Application", formStateAdd.name, "created"),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "create_application",
        application_id: applicationData.id,
        application_name: formStateAdd.name,
        user_id: user?.id,
        user_name: user?.name,
        details: buildDetails(
          {
            name: formStateAdd.name,
            description: formStateAdd.description,
            is_active: formStateAdd.is_active,
            top_bar_color: formStateAdd.top_bar_color,
            omit_org_brand_from_url: formStateAdd.omit_org_brand_from_url,
            exclude_from_default_application:
              formStateAdd.exclude_from_default_application,
            display_order: formStateAdd.display_order,
            logo: formStateAdd.logo,
          },
          APPLICATION_FIELD_SPECS,
          { skipEmpty: true }
        ),
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (error) {
      console.error("Error creating application:", error);
      Toastify("error", "Application not created");
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const errorDesc = {
        message: `Failed to create Application (Application Name: "${formStateAdd.name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: (error as any).response?.status,
        method: "POST",
        url: "/admin/applications",
        category: "drawer",
        action: "create_application",
        application_name: formStateAdd.name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  // Edit Application Handler
  const handleEditApplication = async () => {
    setLoadData(true);
    if (!currentId) {
      Toastify("error", "Invalid application ID. Please select correct application");
      return;
    }

    try {
      const applicationData: any = {
        id: currentId,
        name: formStateEdit.name,
        description: formStateEdit.description,
        is_active: formStateEdit.is_active,
        display_order: formStateEdit.display_order,
        logo: formStateEdit.logo,
        top_bar_color: formStateEdit.top_bar_color || null,
      };

      const storedOmitOrgBrand = readStoredOmitOrgBrand(currentData);
      if (formStateEdit.omit_org_brand_from_url) {
        applicationData.omit_org_brand_from_url = true;
      } else if (storedOmitOrgBrand) {
        // Turned off an application that had opted in: the `false` has to go on
        // the wire so the stored `true` is actually replaced.
        applicationData.omit_org_brand_from_url = false;
      }
      // Otherwise the flag was already off and untouched: don't write a key
      // this application never asked for.

      const storedExcludeFromDefault = readStoredExcludeFromDefault(currentData);
      if (formStateEdit.exclude_from_default_application) {
        applicationData.exclude_from_default_application = true;
      } else if (storedExcludeFromDefault) {
        applicationData.exclude_from_default_application = false;
      }

      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const res = await apiClient.post(`/admin/applications?user_id=${user.id}`, applicationData);

      // Check if brand_exclude is changed
      const originalBrands = currentData?.access?.brand_level?.brands || [];
      const originalBrandsExclude = originalBrands.map((brand: any) => {
        const accessMode = currentData?.access?.brand_level?.access_mode;
        return accessMode === "EXCLUDE" ? -parseInt(brand.id) : parseInt(brand.id);
      });

      const brandChanged = JSON.stringify(originalBrandsExclude.sort()) !== JSON.stringify(formStateEdit.brands_exclude.sort());

      if (brandChanged) {
        const originalAccessMode = currentData?.access?.brand_level?.access_mode || "INCLUDE";
        const originalBrandIds = originalBrandsExclude.map((id: number) => Math.abs(id).toString());

        const excludedBrands = formStateEdit.brands_exclude.filter((id: number) => id < 0).map((id: number) => Math.abs(id));
        const includedBrands = formStateEdit.brands_exclude.filter((id: number) => id > 0);

        let newAccessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
        let newBrandIds: string[] = [];

        if (excludedBrands.length > 0) {
          newAccessMode = "EXCLUDE";
          newBrandIds = excludedBrands.map((id: number) => id.toString());
        } else if (includedBrands.length > 0) {
          newAccessMode = "INCLUDE";
          newBrandIds = includedBrands.map((id: number) => id.toString());
        }

        let brandAccessData: any[] = [];

        if (originalAccessMode !== newAccessMode) {
          for (const brandId of originalBrandIds) {
            let data = {
              brand_id: brandId,
              level_type: "application",
              level_ids: [currentId],
              access_mode: originalAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            brandAccessData.push(data);
          }

          for (const brandId of newBrandIds) {
            let data = {
              brand_id: brandId,
              level_type: "application",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            brandAccessData.push(data);
          }
        } else {
          const addedBrands = newBrandIds.filter((id: string) => !originalBrandIds.includes(id));
          const removedBrands = originalBrandIds.filter((id: string) => !newBrandIds.includes(id));

          for (const brandId of addedBrands) {
            let data = {
              brand_id: brandId,
              level_type: "application",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            brandAccessData.push(data);
          }

          for (const brandId of removedBrands) {
            let data = {
              brand_id: brandId,
              level_type: "application",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            brandAccessData.push(data);
          }
        }

        if (brandAccessData.length > 0) {
          try {
            let payload = {
              data: brandAccessData
            }
            await apiClient.post(`/admin/brand-access?user_id=${user.id}`, payload);
          } catch (brandAccessError) {
            console.error("Error updating brand access:", brandAccessError);
          }
        }
      }

      // Check if organizations_exclude is changed
      const originalOrganizations = currentData?.access?.organization_level?.organizations || [];
      const originalOrganizationsExclude = originalOrganizations.map((org: any) => {
        const accessMode = currentData?.access?.organization_level?.access_mode;
        return accessMode === "EXCLUDE" ? -parseInt(org.id) : parseInt(org.id);
      });

      const organizationChanged = JSON.stringify(originalOrganizationsExclude.sort()) !== JSON.stringify(formStateEdit.organizations_exclude.sort());

      if (organizationChanged) {
        const originalAccessMode = currentData?.access?.organization_level?.access_mode || "INCLUDE";
        const originalOrganizationIds = originalOrganizationsExclude.map((id: number) => Math.abs(id).toString());

        const excludedOrganizations = formStateEdit.organizations_exclude.filter((id: number) => id < 0).map((id: number) => Math.abs(id));
        const includedOrganizations = formStateEdit.organizations_exclude.filter((id: number) => id > 0);

        let newAccessMode: "INCLUDE" | "EXCLUDE" = "INCLUDE";
        let newOrganizationIds: string[] = [];

        if (excludedOrganizations.length > 0) {
          newAccessMode = "EXCLUDE";
          newOrganizationIds = excludedOrganizations.map((id: number) => id.toString());
        } else if (includedOrganizations.length > 0) {
          newAccessMode = "INCLUDE";
          newOrganizationIds = includedOrganizations.map((id: number) => id.toString());
        }

        let organizationAccessData: any[] = [];

        if (originalAccessMode !== newAccessMode) {
          for (const organizationId of originalOrganizationIds) {
            let data = {
              organization_id: organizationId,
              level_type: "application",
              level_ids: [currentId],
              access_mode: originalAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            organizationAccessData.push(data);
          }

          for (const organizationId of newOrganizationIds) {
            let data = {
              organization_id: organizationId,
              level_type: "application",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            organizationAccessData.push(data);
          }
        } else {
          const addedOrganizations = newOrganizationIds.filter((id: string) => !originalOrganizationIds.includes(id));
          const removedOrganizations = originalOrganizationIds.filter((id: string) => !newOrganizationIds.includes(id));

          for (const organizationId of addedOrganizations) {
            let data = {
              organization_id: organizationId,
              level_type: "application",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "add"
            };
            organizationAccessData.push(data);
          }

          for (const organizationId of removedOrganizations) {
            let data = {
              organization_id: organizationId,
              level_type: "application",
              level_ids: [currentId],
              access_mode: newAccessMode,
              is_wildcard: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: user.id,
              modified_by: user.id,
              action: "remove"
            };
            organizationAccessData.push(data);
          }
        }

        if (organizationAccessData.length > 0) {
          try {
            let payload = {
              data: organizationAccessData
            }
            await apiClient.post(`/admin/organization-access?user_id=${user.id}`, payload);
          } catch (organizationAccessError) {
            console.error("Error updating organization access:", organizationAccessError);
          }
        }
      }

      Toastify("success", "Application updated successfully");

      const originalData = {
        name: currentData?.name,
        description: currentData?.description,
        is_active: currentData?.is_active,
        top_bar_color:
          currentData?.top_bar_color || currentData?.metadata?.top_bar_color,
        omit_org_brand_from_url: storedOmitOrgBrand,
        exclude_from_default_application: storedExcludeFromDefault,
        display_order: (currentData as any)?.display_order ?? 0,
        logo: (currentData as any)?.logo || "",
      };
      const nextData = {
        name: formStateEdit?.name,
        description: formStateEdit?.description,
        is_active: formStateEdit?.is_active,
        top_bar_color: formStateEdit?.top_bar_color,
        omit_org_brand_from_url: formStateEdit?.omit_org_brand_from_url,
        exclude_from_default_application:
          formStateEdit?.exclude_from_default_application,
        display_order: formStateEdit?.display_order ?? 0,
        logo: formStateEdit?.logo || "",
      };

      getApplications();
      handleEditDialogClose();

      const changes = diffFields(originalData, nextData, APPLICATION_FIELD_SPECS);
      const brandLookup = [...(brands || []), ...(originalBrands || [])];
      const orgLookup = [
        ...(organizations || []),
        ...(originalOrganizations || []),
      ];
      if (brandChanged) {
        changes.push(
          ...buildAccessLogChanges(
            "Brand",
            originalBrandsExclude,
            formStateEdit.brands_exclude,
            brandLookup,
            {
              previousMode: currentData?.access?.brand_level?.access_mode,
              nextMode: inferNextAccessModeFromSignedIds(
                formStateEdit.brands_exclude,
                currentData?.access?.brand_level?.access_mode
              ),
            }
          )
        );
      }
      if (organizationChanged) {
        changes.push(
          ...buildAccessLogChanges(
            "Organization",
            originalOrganizationsExclude,
            formStateEdit.organizations_exclude,
            orgLookup,
            {
              previousMode:
                currentData?.access?.organization_level?.access_mode,
              nextMode: inferNextAccessModeFromSignedIds(
                formStateEdit.organizations_exclude,
                currentData?.access?.organization_level?.access_mode
              ),
            }
          )
        );
      }
      const desc = {
        message: buildSummary(
          "Application",
          formStateEdit?.name || currentData?.name,
          "updated",
          changes.length
        ),
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        category: "drawer",
        action: "update_application",
        application_id: currentId,
        application_name: formStateEdit?.name || currentData?.name,
        user_id: user?.id,
        user_name: user?.name,
        changes,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (error: any) {
      console.error("Error updating application:", error);
      if (error.response?.data?.detail) {
        const errorMessages = Array.isArray(error.response.data.detail) ? error.response.data.detail.map((error: any) => `${error.loc?.join(".")}: ${error.msg}`).join(", ") : error.response.data.detail;
        Toastify("error", errorMessages);
      } else {
        Toastify("error", "Application not updated");
      }
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const errorDesc = {
        message: `Failed to edit Application (Application ID: ${currentId}, Application Name: "${currentData?.name}") by User (User ID: ${user?.id}, User Name: "${user?.name}")`,
        status: error.response?.status,
        method: "POST",
        url: `/admin/applications?user_id=${user.id}`,
        category: "drawer",
        action: "update_application",
        application_id: currentId,
        application_name: currentData?.name,
        user_id: user?.id,
        user_name: user?.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    } finally {
      setLoadData(false);
    }
  };

  // Dialog Handlers
  const handleAddDialogOpen = () => {
    setIsAddDialogOpen(true);
  };

  const handleAddDialogClose = () => {
    setIsAddDialogOpen(false);
    setFormStateAdd({
      name: "",
      description: "",
      is_active: true,
      display_order: 0,
      logo: "",
      top_bar_color: "#16306D",
      omit_org_brand_from_url: false,
      exclude_from_default_application: false,
    });
  };

  const handleEditOpen = (data: Application) => {
    setCurrentId(data.id);
    setCurrentData(data);

    let brands_exclude: number[] = [];
    let organizations_exclude: number[] = [];

    if (data.access?.brand_level) {
      const brandLevel = data.access.brand_level;
      if (brandLevel.brands && Array.isArray(brandLevel.brands)) {
        if (brandLevel.access_mode === "EXCLUDE") {
          brands_exclude = brandLevel.brands.map((brand: any) => -parseInt(brand.id));
        } else if (brandLevel.access_mode === "INCLUDE") {
          brands_exclude = brandLevel.brands.map((brand: any) => parseInt(brand.id));
        }
      }
    }

    if (data.access?.organization_level) {
      const orgLevel = data.access.organization_level;
      if (orgLevel.organizations && Array.isArray(orgLevel.organizations)) {
        if (orgLevel.access_mode === "EXCLUDE") {
          organizations_exclude = orgLevel.organizations.map((org: any) => -parseInt(org.id));
        } else if (orgLevel.access_mode === "INCLUDE") {
          organizations_exclude = orgLevel.organizations.map((org: any) => parseInt(org.id));
        }
      }
    }

    setFormStateEdit({
      name: data.name,
      description: data.description || "",
      is_active: data.is_active,
      brands_exclude,
      organizations_exclude,
      display_order: (data as any).display_order || 0,
      logo: (data as any).logo || "",
      top_bar_color: (data as any).top_bar_color || (data as any)?.metadata?.top_bar_color || "#16306D",
      omit_org_brand_from_url: readStoredOmitOrgBrand(data),
      exclude_from_default_application: readStoredExcludeFromDefault(data),
    });
    setIsEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
    setCurrentData(null);
    setFormStateEdit({
      name: "",
      description: "",
      is_active: true,
      brands_exclude: [],
      organizations_exclude: [],
      display_order: 0,
      logo: "",
      top_bar_color: "#16306D",
      omit_org_brand_from_url: false,
      exclude_from_default_application: false,
    });
    setCurrentId(null);
  };

  const searchResults = (query: string) => {
    const filteredApplications = [...applicationsBackup];
    const results = filteredApplications.filter((app) =>
      app.name?.toLowerCase().includes(query.toLowerCase()) ||
      app.description?.toLowerCase().includes(query.toLowerCase())
    );
    setApplications(results);
    setPage(0); // Reset to first page on search
  };

  useEffect(() => {
    searchResults(searchText);
  }, [searchText, applicationsBackup]);

  // Handle openDialog from parent component
  useEffect(() => {
    if (openDialog) {
      handleAddDialogOpen();
      setOpenDialog(false);
    }
  }, [openDialog, setOpenDialog]);

  // Color validation function
  const isValidHexColor = (color: string) => {
    return /^#[0-9A-F]{6}$/i.test(color);
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-end mb-2">
            <Info className="h-4 w-4 text-black" />
            <p className="ml-2 text-sm text-black">
              Please create a{" "}
              <a href="https://feedback.lift.newengen.com/" target="_blank" rel="noopener noreferrer" className="underline text-blue-500 hover:no-underline">ticket</a>
              {" "}for any additions or modifications
            </p>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            <DataTable
              className="flex-1 min-h-0 overflow-auto"
              maxHeight="100%"
              columns={(() => {
                const cols: TableColumn<any>[] = [
                  { key: "name", header: "Application Name", accessor: "name", width: "180px", render: (_v: any, row: any) => <span className="font-medium">{row.name}</span> },
                  { key: "description", header: "Description", accessor: (row: any) => row.description || "--", width: "200px", render: (_v: any, row: any) => <span className="text-gray-600 max-w-[200px] truncate block">{row.description || "--"}</span> },
                  { key: "logo", header: "Logo", accessor: (row: any) => row.logo ? row.logo.split('/').pop() : "--", width: "120px", render: (_v: any, row: any) => <span className="text-gray-600">{row.logo ? row.logo.split('/').pop() : "--"}</span> },
                  { key: "top_bar_color", header: "Top Bar Color", accessor: (row: any) => row.top_bar_color || row.metadata?.top_bar_color, width: "140px", align: "center", render: (_v: any, row: any) => (row.top_bar_color || row.metadata?.top_bar_color) ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 rounded border border-gray-300" style={{ backgroundColor: row.top_bar_color || row.metadata?.top_bar_color }} />
                      <span className="text-xs">{row.top_bar_color || row.metadata?.top_bar_color}</span>
                    </div>
                  ) : "--" },
                  { key: "exclude_from_default_application", header: "Exclude from default", accessor: (row: any) => readStoredExcludeFromDefault(row), width: "160px", align: "center", render: (_v: any, row: any) => readStoredExcludeFromDefault(row) ? "Yes" : "No" },
                  { key: "brands", header: "Excluded/Include Brands", accessor: (row: any) => row.id, width: "200px", render: (_v: any, row: any) => renderBrandBadges(row) },
                  { key: "organizations", header: "Excluded/Include Organizations", accessor: (row: any) => row.id, width: "200px", render: (_v: any, row: any) => renderOrganizationBadges(row) },
                  { key: "display_order", header: "Order", accessor: (row: any) => (row as any).display_order || 0, width: "80px", align: "center" },
                  { key: "status", header: "Status", accessor: (row: any) => row.is_active, width: "100px", align: "center", render: (_v: any, row: any) => renderStatusBadge(row.is_active) },
                  { key: "created_at", header: "Created", accessor: (row: any) => formatDate(row.created_at), width: "120px", render: (_v: any, row: any) => <span className="text-gray-600">{formatDate(row.created_at)}</span> },
                ];
                if (showActionsColumn) {
                  cols.push({
                    key: "actions",
                    header: "Actions",
                    accessor: (row: any) => row.id,
                    width: "100px",
                    align: "center",
                    render: (_v: any, row: any) => (
                      <div className="flex gap-1 justify-center">
                        {hasEditAccess && (
                          <Button variant="ghost" size="sm" onClick={() => handleEditOpen(row)} className="h-8 w-8 p-0">
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ),
                  });
                }
                return cols;
              })()}
              data={paginatedData}
              loading={loadData}
              emptyMessage="No applications found."
              stickyHeader
              showActions={false}
              pagination={{
                currentPage: page + 1,
                totalPages: Math.max(1, totalPages),
                pageSize: rowsPerPage,
                totalItems: rowData.length,
                onPageChange: (p) => setPage(p - 1),
                onPageSizeChange: (size) => { setRowsPerPage(Number(size)); setPage(0); },
                pageSizeOptions: [10, 20, 50, 100],
              }}
            />
          </div>
        </div>

        {/* Add Application Sheet */}
        <Sheet open={isAddDialogOpen} onOpenChange={(open) => !open && handleAddDialogClose()}>
          <SheetContent className="sm:max-w-[540px] flex flex-col h-full">
            <SheetHeader>
              <SheetTitle>Add New Application</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto min-h-0">
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="add-app-name">Application Name *</Label>
                <Input
                  id="add-app-name"
              value={formStateAdd.name}
              onChange={(e) =>
                setFormStateAdd((prev: FormStateAdd) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
                  placeholder="Enter application name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-app-description">Description</Label>
                <Textarea
                  id="add-app-description"
              value={formStateAdd.description}
              onChange={(e) =>
                setFormStateAdd((prev: FormStateAdd) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
                  placeholder="Enter description"
              rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-app-logo">Logo</Label>
                <Input
                  id="add-app-logo"
              value={formStateAdd.logo}
              onChange={(e) =>
                setFormStateAdd((prev: FormStateAdd) => ({
                  ...prev,
                  logo: e.target.value,
                }))
              }
              placeholder="Enter logo URL or path"
            />
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-app-order">Display Order</Label>
                <Input
                  id="add-app-order"
              type="number"
              value={formStateAdd.display_order}
              onChange={(e) =>
                setFormStateAdd((prev: FormStateAdd) => ({
                  ...prev,
                  display_order: parseInt(e.target.value) || 0,
                }))
              }
            />
              </div>

              <div className="space-y-2">
                <Label>Top Bar Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formStateAdd.top_bar_color}
                    onChange={(e) =>
                      setFormStateAdd((prev: FormStateAdd) => ({
                        ...prev,
                        top_bar_color: e.target.value,
                      }))
                    }
                    className="w-12 h-10 border-none rounded cursor-pointer"
                  />
                  <Input
                    value={formStateAdd.top_bar_color}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || isValidHexColor(value)) {
                        setFormStateAdd((prev: FormStateAdd) => ({
                          ...prev,
                          top_bar_color: value,
                        }));
                      }
                    }}
                    className="w-28"
                    placeholder="#16306D"
                  />
                </div>
                {formStateAdd.top_bar_color !== "" && !isValidHexColor(formStateAdd.top_bar_color) && (
                  <p className="text-sm text-red-500">Invalid hex color</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="add-app-omit-org-brand"
                  checked={formStateAdd.omit_org_brand_from_url}
                  onCheckedChange={(checked) =>
                    setFormStateAdd((prev: FormStateAdd) => ({
                      ...prev,
                      omit_org_brand_from_url: checked,
                    }))
                  }
                />
                <Label htmlFor="add-app-omit-org-brand">Omit org/brand from URL</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="add-app-exclude-from-default"
                  checked={formStateAdd.exclude_from_default_application}
                  onCheckedChange={(checked) =>
                    setFormStateAdd((prev: FormStateAdd) => ({
                      ...prev,
                      exclude_from_default_application: checked,
                    }))
                  }
                />
                <Label htmlFor="add-app-exclude-from-default">Exclude from default application</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="add-app-active"
                  checked={formStateAdd.is_active}
                  onCheckedChange={(checked) =>
                    setFormStateAdd((prev: FormStateAdd) => ({
                      ...prev,
                      is_active: checked,
                    }))
                  }
                />
                <Label htmlFor="add-app-active">Active</Label>
              </div>
            </div>
            </div>
            <SheetFooter className="flex gap-2 pt-4 border-t flex-shrink-0 mt-auto">
              <Button onClick={handleAddDialogClose} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={handleAddApplication}
              disabled={!formStateAdd.name.trim() || (formStateAdd.top_bar_color !== "" && !isValidHexColor(formStateAdd.top_bar_color))}
            >
              Create
            </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Edit Application Sheet */}
        <Sheet open={isEditDialogOpen} onOpenChange={(open) => !open && handleEditDialogClose()}>
          <SheetContent className="sm:max-w-[540px] flex flex-col h-full">
            <SheetHeader>
              <SheetTitle>Edit Application</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto min-h-0">
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-app-name">Application Name *</Label>
                <Input
                  id="edit-app-name"
              value={formStateEdit.name}
              onChange={(e) =>
                setFormStateEdit((prev: FormStateEdit) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
                  placeholder="Enter application name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-app-description">Description</Label>
                <Textarea
                  id="edit-app-description"
              value={formStateEdit.description}
              onChange={(e) =>
                setFormStateEdit((prev: FormStateEdit) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
                  placeholder="Enter description"
              rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-app-logo">Logo</Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="edit-app-logo"
                    value={formStateEdit.logo}
                    onChange={(e) =>
                      setFormStateEdit((prev: FormStateEdit) => ({
                        ...prev,
                        logo: e.target.value,
                      }))
                    }
                    placeholder="Enter logo URL or path"
                    className="flex-1 min-w-0"
                  />
                  {formStateEdit.logo && (
                    <div className="flex-shrink-0 w-10 h-10 rounded border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center">
                      <img
                        src={formStateEdit.logo}
                        alt="Logo preview"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-app-order">Display Order</Label>
                <Input
                  id="edit-app-order"
              type="number"
              value={formStateEdit.display_order}
              onChange={(e) =>
                setFormStateEdit((prev: FormStateEdit) => ({
                  ...prev,
                  display_order: parseInt(e.target.value) || 0,
                }))
              }
            />
              </div>

              <div className="space-y-2">
                <Label>Top Bar Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formStateEdit.top_bar_color}
                    onChange={(e) =>
                      setFormStateEdit((prev: FormStateEdit) => ({
                        ...prev,
                        top_bar_color: e.target.value,
                      }))
                    }
                    className="w-12 h-10 border-none rounded cursor-pointer"
                  />
                  <Input
                    value={formStateEdit.top_bar_color}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || isValidHexColor(value)) {
                        setFormStateEdit((prev: FormStateEdit) => ({
                          ...prev,
                          top_bar_color: value,
                        }));
                      }
                    }}
                    className="w-28"
                    placeholder="#16306D"
                  />
                </div>
                {formStateEdit.top_bar_color !== "" && !isValidHexColor(formStateEdit.top_bar_color) && (
                  <p className="text-sm text-red-500">Invalid hex color</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-app-omit-org-brand"
                  checked={formStateEdit.omit_org_brand_from_url}
                  onCheckedChange={(checked) =>
                    setFormStateEdit((prev: FormStateEdit) => ({
                      ...prev,
                      omit_org_brand_from_url: checked,
                    }))
                  }
                />
                <Label htmlFor="edit-app-omit-org-brand">Omit org/brand from URL</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-app-exclude-from-default"
                  checked={formStateEdit.exclude_from_default_application}
                  onCheckedChange={(checked) =>
                    setFormStateEdit((prev: FormStateEdit) => ({
                      ...prev,
                      exclude_from_default_application: checked,
                    }))
                  }
                />
                <Label htmlFor="edit-app-exclude-from-default">Exclude from default application</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-app-active"
                  checked={formStateEdit.is_active}
                  onCheckedChange={(checked) =>
                    setFormStateEdit((prev: FormStateEdit) => ({
                      ...prev,
                      is_active: checked,
                    }))
                  }
                />
                <Label htmlFor="edit-app-active">Active</Label>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-4">Manage Access</h3>

                <Collapsible className="mb-6">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Brand Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific brands. Use positive IDs (e.g., 1, 2) to include only those specific brands.
                    </p>
                    <BrandMultiSelect
                      defaultBrands={formStateEdit.brands_exclude}
                      setDefaultBrands={(field: string, value: number[]) => {
                        setFormStateEdit((prev) => ({
                          ...prev,
                          brands_exclude: value,
                        }));
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>

                <Collapsible className="mb-8">
                  <CollapsibleTrigger className="flex items-center justify-between w-full rounded-lg border border-input bg-background px-4 py-3 text-left font-medium hover:bg-accent/50 h-10 [&[data-state=open]>svg]:rotate-180">
                    <span>Organization Access</span>
                    <ChevronDown className="h-4 w-4 shrink-0 transition-transform" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <p className="text-sm text-muted-foreground flex items-center mb-3">
                      <Info className="h-4 w-4 shrink-0 mr-1" />
                      Use negative IDs (e.g., -1, -2) to exclude specific organizations. Use positive IDs (e.g., 1, 2) to include only those specific organizations.
                    </p>
                    <OrganizationMultiSelect
                      defaultOrganizations={formStateEdit.organizations_exclude}
                      setDefaultOrganizations={(field: string, value: number[]) => {
                        setFormStateEdit((prev) => ({
                          ...prev,
                          organizations_exclude: value,
                        }));
                      }}
                    />
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
            </div>
            <SheetFooter className="flex gap-2 pt-4 border-t flex-shrink-0 mt-auto">
              <Button onClick={handleEditDialogClose} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={handleEditApplication}
              disabled={!formStateEdit.name.trim() || loadData || (formStateEdit.top_bar_color !== "" && !isValidHexColor(formStateEdit.top_bar_color))}
            >
              {loadData ? 'Updating...' : 'Update'}
            </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </ThemeProvider>
    </>
  );
};

export default Applications;
